import { Link } from "react-router-dom";
import { useT } from "@/i18n/LocaleContext";

export default function Footer() {
  const { t, prefix } = useT();
  return (
    <footer className="border-t border-[#E3E8EC] bg-white" data-testid="main-footer" itemScope itemType="https://schema.org/LocalBusiness">
      <meta itemProp="name" content="Hamm Entwicklung + Produktion GmbH" />
      <meta itemProp="alternateName" content="Hamm EP GmbH" />
      <meta itemProp="url" content="https://www.hamm.gmbh/" />
      <meta itemProp="vatID" content="DE287921642" />
      <meta itemProp="areaServed" content="DE, AT, CH, EU" />
      <meta itemProp="priceRange" content="B2B – auf Anfrage" />
      <link itemProp="image" href="https://www.hamm.gmbh/assets/hero-laser.jpg" />
      <link itemProp="logo" href="https://www.hamm.gmbh/assets/hamm-logo.png" />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-3 gap-14">
        <div>
          <img src="/assets/hamm-logo.png" alt="Logo der Hamm Entwicklung + Produktion GmbH – Blechbearbeitung und Edelstahl-Systemkomponenten aus Geldern" className="h-14 w-auto" />
          <p className="mt-6 text-sm text-[var(--ci-grey)] leading-relaxed max-w-xs" itemProp="description">
            {t("footer.tagline")}
          </p>
        </div>

        <div>
          <h4 className="font-tech text-[10px] tracking-[0.25em] uppercase text-[var(--ci-grey)]">{t("footer.contact")}</h4>
          <address className="not-italic mt-6 space-y-2.5 text-sm text-[var(--ci-grey)]" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
            <p>
              <strong className="block font-semibold" itemProp="name">Hamm Entwicklung + Produktion GmbH</strong>
              <span itemProp="streetAddress">Max-Planck-Str. 13</span><br />
              <span itemProp="postalCode">47608</span> <span itemProp="addressLocality">Geldern</span><br />
              <span itemProp="addressRegion">Nordrhein-Westfalen</span>, <span itemProp="addressCountry">Deutschland</span>
            </p>
            <p>
              <a href="mailto:verkauf@hamm.gmbh" itemProp="email" className="text-[var(--ci-blue)] hover:text-[var(--ci-turquoise)] transition-colors" data-testid="footer-email">verkauf@hamm.gmbh</a>
            </p>
            <p>
              <a href="tel:+4928319901500" itemProp="telephone" className="text-[var(--ci-blue)] hover:text-[var(--ci-turquoise)] transition-colors" data-testid="footer-phone">+49 2831 9901-500</a>
            </p>
          </address>
          {/* Geo-Koordinaten – stärken lokale SEO für „Blechbearbeitung Geldern" */}
          <div itemProp="geo" itemScope itemType="https://schema.org/GeoCoordinates" className="sr-only">
            <meta itemProp="latitude" content="51.5187" />
            <meta itemProp="longitude" content="6.3247" />
          </div>
          <p className="mt-4 font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)]">
            Öffnungszeiten: Mo–Fr <time itemProp="openingHours" dateTime="Mo-Fr 07:30-17:00">07:30–17:00</time>
          </p>
        </div>

        <div>
          <h4 className="font-tech text-[10px] tracking-[0.25em] uppercase text-[var(--ci-grey)]">{t("footer.navigation")}</h4>
          <ul className="mt-6 space-y-2.5 text-sm text-[var(--ci-grey)]">
            <li><a href={`${prefix}#hygiene`} className="hover:text-[var(--ci-turquoise)] transition-colors">{t("nav.hygiene")}</a></li>
            <li><a href={`${prefix}#branchen`} className="hover:text-[var(--ci-turquoise)] transition-colors">{t("nav.industries")}</a></li>
            <li><a href={`${prefix}#leistungen`} className="hover:text-[var(--ci-turquoise)] transition-colors">{t("nav.services")}</a></li>
            <li><a href={`${prefix}#normen`} className="hover:text-[var(--ci-turquoise)] transition-colors">{t("nav.standards")}</a></li>
            <li><a href={`${prefix}#maschinenpark`} className="hover:text-[var(--ci-turquoise)] transition-colors">{t("nav.machines")}</a></li>
            <li><Link to={prefix === "/en" ? "/en/inquiry-portal" : "/anfrage-portal"} className="hover:text-[var(--ci-turquoise)] transition-colors" data-testid="footer-link-anfrage">{t("nav.inquiry")}</Link></li>
            <li><Link to="/karriere" className="hover:text-[var(--ci-turquoise)] transition-colors" data-testid="footer-link-karriere">Karriere</Link></li>
            <li><Link to="/admin/login" className="hover:text-[var(--ci-turquoise)] transition-colors" data-testid="footer-admin-link">{t("footer.adminLink")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#E3E8EC]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)]">{t("footer.copyright")}</p>
          <div className="flex items-center gap-6">
            <Link to="/impressum" className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] transition-colors" data-testid="footer-link-impressum">Impressum</Link>
            <Link to="/datenschutz" className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] transition-colors" data-testid="footer-link-datenschutz">Datenschutz</Link>
            <Link to="/agb" className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] transition-colors" data-testid="footer-link-agb">AGB</Link>
            <p className="font-tech text-[10px] tracking-[0.2em] uppercase text-[#B9C2CA]">{t("footer.claim")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
